/**
 * Money helpers. Invariant 2: money is Decimal(12,2), never a JavaScript number.
 *
 * A float touching a rent balance is a bug even when the test passes, so the
 * zod schema below refuses a `number` input outright rather than coercing it.
 * Transport is a decimal string; arithmetic is decimal.js / Prisma.Decimal.
 */
import { Decimal } from 'decimal.js';
import { z } from 'zod';
export const MONEY_SCALE = 2;
export const MONEY_PRECISION = 12;
/** Largest value Decimal(12,2) can hold: 10 integer digits, 2 fractional. */
export const MONEY_MAX = new Decimal('9999999999.99');
const MONEY_PATTERN = /^-?\d{1,10}(\.\d{1,2})?$/;
/**
 * A money amount on the wire. Accepts a decimal string or a Decimal, never a
 * number — `z.coerce.number()` here would quietly reintroduce float error.
 */
export const moneySchema = z
    .union([z.string(), z.instanceof(Decimal)])
    .superRefine((value, ctx) => {
    if (typeof value === 'string' && !MONEY_PATTERN.test(value.trim())) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Expected a decimal amount with at most 2 decimal places, e.g. "1250.00".',
        });
    }
})
    .transform((value) => new Decimal(typeof value === 'string' ? value.trim() : value))
    .superRefine((value, ctx) => {
    if (!value.isFinite()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Amount must be finite.' });
        return;
    }
    if (value.decimalPlaces() > MONEY_SCALE) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Amount may not have more than ${MONEY_SCALE} decimal places.`,
        });
    }
    if (value.abs().greaterThan(MONEY_MAX)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Amount exceeds Decimal(${MONEY_PRECISION},${MONEY_SCALE}).`,
        });
    }
});
/** A money amount that must be strictly greater than zero (a charge, a payment). */
export const positiveMoneySchema = moneySchema.refine((v) => v.greaterThan(0), {
    message: 'Amount must be greater than zero.',
});
/** A money amount that may be zero but not negative (a balance, a deposit). */
export const nonNegativeMoneySchema = moneySchema.refine((v) => v.greaterThanOrEqualTo(0), {
    message: 'Amount may not be negative.',
});
/** Serialize for transport. Always fixed scale so "800" and "800.00" compare equal. */
export function toMoneyString(value) {
    return new Decimal(value).toFixed(MONEY_SCALE);
}
/** Sum with no intermediate rounding. */
export function sumMoney(values) {
    return values
        .reduce((acc, v) => acc.plus(v), new Decimal(0))
        .toDecimalPlaces(MONEY_SCALE);
}
export { Decimal };
//# sourceMappingURL=money.js.map